import React from 'react';
import { ButtonLink } from '@brighterion/br-ui';
import { handleOnClickEvent, handleKeyEvent } from 'Utils';
import { Row } from 'react-table';
import clsx from 'clsx';
import styles from './Navigation.css';

interface Props<D extends object> {
    dataTestId: string;
    selectedRow: Row<D>;
    rows: Row<D>[];
    toggleRowSelected(id: string, toggle: boolean): void;
    leftElement?: React.ReactNode;
}

const Navigation = <D extends object = {}>({
    dataTestId,
    selectedRow,
    rows,
    toggleRowSelected,
    leftElement
}: Props<D>) => {
    const [isLast, setIsLast] = React.useState(rows.length <= 1);
    const [isFirst, setIsFirst] = React.useState(rows.length <= 1);

    React.useEffect(() => {
        if (selectedRow) {
            const nextRowDom = document.querySelector(`[data-testid="row-${selectedRow.id}"]`);
            nextRowDom?.scrollIntoView({ block: 'nearest' });
        }

        // check if next or previous need to be disabled
        const selectedFlatRowIndex = rows.findIndex((r) => r.id === selectedRow.id);
        const nextRow = rows[selectedFlatRowIndex + 1];
        const prevRow = rows[selectedFlatRowIndex - 1];

        if (!nextRow) {
            setIsLast(true);
        } else if (nextRow.isGrouped) {
            const nextNextRow = nextRow.subRows[0];
            setIsLast(!nextNextRow);
        } else {
            setIsLast(false);
        }
        if (!prevRow) {
            setIsFirst(true);
        } else if (prevRow.isGrouped) {
            const prevPrevRow = rows[selectedFlatRowIndex - 2];
            setIsFirst(!prevPrevRow);
        } else {
            setIsFirst(false);
        }
    }, [selectedRow, rows]);

    const navHandler = (next: boolean) => {
        const selectedFlatRowIndex = rows.findIndex((r) => r.id === selectedRow.id);
        const increment = next ? 1 : -1;
        const nextIndex = selectedFlatRowIndex + increment;
        let nextRow = rows[nextIndex];

        if (!nextRow) return;

        // if nextRow is grouped and is previous row then walk back one more to grab the correct previous row
        if (nextRow.isGrouped && !next) {
            nextRow = rows[nextIndex - 1];
            if (!nextRow) return;
        }

        if (nextRow.isGrouped) {
            if (!nextRow.isExpanded) {
                nextRow.toggleRowExpanded();
            }
            const nextSubRowIndex = next ? 0 : nextRow.subRows.length - 1;
            toggleRowSelected(nextRow.subRows[nextSubRowIndex].id, true);
        } else {
            nextRow?.toggleSplitView();
        }
    };

    const handleNavigation = (next: boolean) => {
        navHandler(next);
    };

    return (
        <div className={clsx(styles.splitViewNav, !!leftElement && styles.spaceBetween)} data-testid={dataTestId}>
            {leftElement || null}
            <div className={styles.navButtonLink}>
                <ButtonLink
                    dataTestId={`${dataTestId}-prev`}
                    className={clsx(styles.navButtonLink, styles.leftButton)}
                    size='large'
                    name='Previous Rule'
                    url='_blank'
                    clickCb={handleOnClickEvent(() => handleNavigation(false))}
                    keydownCb={handleKeyEvent(() => handleNavigation(false))}
                    iconName='chevron-left'
                    disabled={isFirst}
                />
                <ButtonLink
                    dataTestId={`${dataTestId}-next`}
                    className={clsx(styles.navButtonLink, styles.rightButton)}
                    size='large'
                    name='Next Rule'
                    url='_blank'
                    clickCb={handleOnClickEvent(() => handleNavigation(true))}
                    keydownCb={handleKeyEvent(() => handleNavigation(true))}
                    iconName='chevron-right'
                    disabled={isLast}
                />
            </div>
        </div>
    );
};

export default Navigation;
